import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { CalendarDays, ChevronRight, LockKeyhole, RefreshCw, Truck, X } from "lucide-react";
import LanguageSelector from "./LanguageSelector.jsx";

function apiUrl(path) {
  try {
    const host = String(window.location.hostname || "").toLowerCase();
    const protocol = String(window.location.protocol || "").toLowerCase();
    const nativeLike = protocol === "capacitor:" || protocol === "file:" || host === "localhost" || host === "127.0.0.1";
    return nativeLike ? `https://backend.cargo-platform.online${path}` : path;
  } catch {
    return path;
  }
}

async function getJson(path) {
  const res = await fetch(apiUrl(path), {
    method: "GET",
    credentials: "include",
    cache: "no-store",
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data?.error || data?.message || `HTTP_${res.status}`);
  return data;
}

function guestMarketSafeError(rawError, t, { detail = false } = {}) {
  const code = String(rawError?.message || rawError || "").trim();
  const upperCode = code.toUpperCase();

  if (
    upperCode.includes("RADAR_SNAPSHOT_STALE") ||
    /^HTTP[_ ]?(502|503|504)(?:\b|$)/.test(upperCode) ||
    upperCode.includes("SERVICE_UNAVAILABLE") ||
    upperCode.includes("GATEWAY_TIMEOUT")
  ) {
    return t("freightMarket.refreshingHint", {
      defaultValue: "Обновляем список грузов. Подождите несколько секунд или нажмите «Обновить».",
    });
  }

  if (detail && upperCode.includes("DRIVER_MARKET_IN_PROGRESS")) {
    return t("guestMarket.marketInProgress", {
      defaultValue: "Этот груз сейчас обрабатывается. Выберите другой или попробуйте позже.",
    });
  }

  if (
    detail &&
    (upperCode.includes("DRIVER_MARKET_NOT_AVAILABLE") ||
      upperCode.includes("RADAR_FREIGHT_NOT_ACTIVE") ||
      upperCode.includes("MARKET_FREIGHT_NOT_ACTIVE"))
  ) {
    return t("guestMarket.marketUnavailable", {
      defaultValue: "Груз уже недоступен или снят с рынка.",
    });
  }

  return detail
    ? t("freightMarket.detailLoadErrorHint", {
        defaultValue: "Не удалось открыть груз. Обновите список и попробуйте снова.",
      })
    : t("freightMarket.loadErrorHint", {
        defaultValue: "Не удалось обновить список грузов. Проверьте интернет и нажмите «Обновить».",
      });
}

function dateText(value) {
  if (!value) return "—";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "—";
  return new Intl.DateTimeFormat(undefined, { day: "2-digit", month: "2-digit", year: "numeric" }).format(d);
}

function loadingDateText(value) {
  const raw = String(value || "").trim();
  if (!/^\d{4}-\d{2}-\d{2}$/.test(raw)) return "—";
  const [y, m, d] = raw.split("-");
  return `${d}.${m}.${y}`;
}

function weightText(value, external, t) {
  const n = Number(value);
  if (!Number.isFinite(n) || n <= 0) return "—";
  if (external) {
    const tons = n / 1000;
    return `${Number.isInteger(tons) ? tons.toFixed(0) : tons.toFixed(1)} ${t("guestMarket.ton")}`;
  }
  return `${new Intl.NumberFormat("ru-RU").format(Math.round(n))} ${t("units.kg")}`;
}

function nativePrice(order, t) {
  const meta = order?.meta || {};
  const amount = Number(meta.priceAmount ?? meta.priceRub ?? 0);
  if (!(amount > 0)) return "—";
  const cur = String(meta.priceCurrency || (meta.priceRub ? "RUB" : "")).toUpperCase();
  const sym = cur === "USD" ? "$" : cur === "EUR" ? "€" : cur === "UZS" ? t("units.uzs") : "₽";
  return `${new Intl.NumberFormat("ru-RU").format(Math.round(amount))} ${sym}`;
}

function nativeCargo(order) {
  return String(order?.cargoName || order?.cargo || order?.title || "").trim() || "—";
}

function externalCargo(item) {
  return String(item?.cargoDisplay || item?.cargo || "").trim() || "";
}

// REISOR_GUEST_SHOWCASE_QUALITY_V3
function guestText(value) {
  return String(value || "").replace(/\s+/g, " ").trim();
}

function guestCleanPlace(value) {
  let s = guestText(value);
  if (!s) return "";

  // Telegram decoration only. Source data itself is never rewritten.
  s = s
    .replace(/^[\s>*•▪▫◾◽🔹🔸❗🔥🌎📍📌]+/gu, "")
    .replace(/[\s>*•▪▫◾◽🔹🔸❗🔥🌎📍📌]+$/gu, "")
    .replace(/^[—–-]+\s*/u, "")
    .replace(/\s*[—–-]+$/u, "")
    .replace(/\s+/g, " ")
    .trim();

  return s;
}

function guestBadPlace(value) {
  const s = guestCleanPlace(value);
  if (!s) return true;

  const low = s.toLocaleLowerCase();

  const badWords = [
    "срочно",
    "срочные",
    "бугун",
    "сегодня",
    "завтра",
    "растамож",
    "готов",
    "тайёр",
    "тайер",
    "таёр",
    "tayyor",
    "tayor",
    "машина нужна",
    "машины нужны",
    "фура нужна",
    "фуры нужны",
    "загрузка",
    "выгрузка",
    "ставка",
    "цена",
    "телефон",
    "диспетчер",
    "менеджер",
    "склад поставки",
    "юкланиш",
  ];

  if (badWords.some((word) => low.includes(word))) return true;

  if (
    s.includes("→") ||
    s.includes("->") ||
    s.includes("\n") ||
    s.includes("$") ||
    s.includes("₽") ||
    s.includes("€")
  ) {
    return true;
  }

  if (/\b(?:ооо|ип|мчж|llc)\b/iu.test(s)) return true;

  // Obvious patronymic/person-name fragments parsed as geography.
  if (
    /(?:ovich|evich|ovna|evna)\b/iu.test(s) ||
    /\b(?:qizi|o['’]?g['’]?li)\b/iu.test(s)
  ) {
    return true;
  }

  // "26 август" etc. is a date, not destination.
  if (/^\d{1,2}\s+(?:январ|феврал|март|апрел|ма[йя]|июн|июл|август|сентябр|октябр|ноябр|декабр)/iu.test(s)) {
    return true;
  }

  if (s.length > 55) return true;
  if (s.split(/\s+/).length > 7) return true;

  return false;
}

function guestCleanCargo(item) {
  const raw = guestText(item?.cargoDisplay || item?.cargo);
  if (!raw) return "";

  const low = raw
    .toLocaleLowerCase()
    .replace(/[.!🔥]+$/gu, "")
    .trim();

  const bad = new Set([
    "готов",
    "готово",
    "тайёр",
    "тайер",
    "таёр",
    "tayyor",
    "tayor",
    "tayor",
    "yuk kere",
    "юк керак",
    "юк кере",
    "груз нужен",
    "машина нужна",
    "фура нужна",
  ]);

  if (bad.has(low)) return "";

  if (
    low.includes("машина нужна") ||
    low.includes("машины нужны") ||
    low.includes("фура нужна") ||
    low.includes("фуры нужны") ||
    low.includes("срочно") ||
    low.includes("телефон")
  ) {
    return "";
  }

  // Weight accidentally parsed as cargo.
  if (/^\d+(?:[.,]\d+)?\s*(?:т|тн|тонн|тонна|тонны|kg|кг)$/iu.test(low)) {
    return "";
  }

  if (low.length < 2 || low.length > 70) return "";

  return raw;
}

function guestRealDate(item) {
  const values = [
    guestText(item?.loadingDate),
    guestText(item?.loadingDisplay),
  ];

  for (const value of values) {
    if (/^20\d{2}-\d{2}-\d{2}$/u.test(value)) return value;
    if (/^\d{1,2}[./-]\d{1,2}[./-]20\d{2}$/u.test(value)) return value;
  }

  return "";
}

function guestReadyLoading(item) {
  const values = [
    guestText(item?.loadingDisplay),
    guestText(item?.loadingDate),
  ];

  const readyValues = new Set([
    "тайёр",
    "тайер",
    "таёр",
    "tayyor",
    "tayor",
    "готов",
    "готово",
    "ready",
  ]);

  return values.some((value) => {
    const normalized = value
      .toLocaleLowerCase()
      .replace(/[.!🔥]+$/gu, "")
      .trim();

    return readyValues.has(normalized);
  });
}

function guestFormatLoadingDate(value) {
  const s = guestText(value);
  if (!s) return "";

  const iso = s.match(/^(20\d{2})-(\d{2})-(\d{2})$/u);
  if (iso) return `${iso[3]}.${iso[2]}.${iso[1]}`;

  const local = s.match(/^(\d{1,2})[./-](\d{1,2})[./-](20\d{2})$/u);
  if (local) {
    return `${local[1].padStart(2, "0")}.${local[2].padStart(2, "0")}.${local[3]}`;
  }

  return "";
}

function guestPrice(item) {
  const value = guestText(item?.ratePrimary || item?.rateHint);

  // Guest showcase must never display an empty price.
  if (!value || value === "—" || value === "-") {
    return "Договорная";
  }

  const low = value.toLocaleLowerCase();

  // Normalize negotiated-price labels into one clear Russian label.
  if (
    low.includes("келишилади") ||
    low.includes("договор")
  ) {
    return "Договорная";
  }

  // Preserve a real numeric/currency price exactly as received.
  if (/\d/u.test(value)) {
    return value;
  }

  return "Договорная";
}

function guestWeightGood(item) {
  const n = Number(item?.weightKg || 0);
  return Number.isFinite(n) && n >= 500 && n <= 100000;
}

function prepareGuestMarketItem(item) {
  if (!item || typeof item !== "object") return null;

  const from = guestCleanPlace(item.from);
  const to = guestCleanPlace(item.to);

  if (
    guestBadPlace(from) ||
    guestBadPlace(to) ||
    from.toLocaleLowerCase() === to.toLocaleLowerCase()
  ) {
    return null;
  }

  if (!guestWeightGood(item)) return null;

  const cargo = guestCleanCargo(item);
  const date = guestRealDate(item);
  const ready = guestReadyLoading(item);
  const loadingDisplay = date
    ? guestFormatLoadingDate(date)
    : ready
      ? "Груз готов"
      : "";
  const price = guestPrice(item);

  // Public showcase:
  // route + weight + meaningful cargo + loading state are mandatory.
  // No empty "Загрузка —" cards are allowed.
  if (!cargo) return null;
  if (!loadingDisplay) return null;

  const score =
    100 +
    (price ? 30 : 0) +
    (date ? 20 : 0) +
    (!date && ready ? 10 : 0);

  return {
    ...item,

    // Presentation layer only. Radar/source data stays untouched.
    from,
    to,

    cargo,
    cargoDisplay: cargo,

    // Preserve true raw date separately.
    loadingDate: date,
    // Guest card gets only a formatted date or "Груз готов".
    loadingDisplay,

    ratePrimary: price,
    rateHint: price,

    _guestQualityScore: score,
  };
}

function guestDedupeKey(item) {
  return [
    guestText(item?.from).toLocaleLowerCase(),
    guestText(item?.to).toLocaleLowerCase(),
    guestText(item?.cargoDisplay || item?.cargo).toLocaleLowerCase(),
    String(Math.round(Number(item?.weightKg || 0))),
    guestText(item?.ratePrimary || item?.rateHint).toLocaleLowerCase(),
  ].join("|");
}

function GateModal({ onClose, onLogin, onRegister }) {
  const { t } = useTranslation();
  return (
    <div className="fixed inset-0 z-[20000] flex items-end justify-center bg-black/75 p-0 sm:items-center sm:p-4">
      <div className="w-full max-w-lg rounded-t-3xl border border-slate-700 bg-slate-950 p-5 text-white shadow-2xl sm:rounded-3xl">
        <div className="flex items-start gap-3">
          <div className="rounded-2xl bg-blue-500/15 p-3 text-blue-300"><LockKeyhole className="h-6 w-6" /></div>
          <div className="min-w-0 flex-1">
            <div className="text-xl font-black">{t("guestMarket.gateTitle")}</div>
            <div className="mt-2 text-sm leading-6 text-slate-300">{t("guestMarket.gateText")}</div>
          </div>
          <button type="button" onClick={onClose} className="rounded-xl bg-slate-800 p-2"><X className="h-5 w-5" /></button>
        </div>
        <div className="mt-5 grid grid-cols-1 gap-2 sm:grid-cols-2">
          <button type="button" onClick={onRegister} className="rounded-2xl bg-blue-600 px-4 py-4 text-base font-black">{t("guestMarket.register")}</button>
          <button type="button" onClick={onLogin} className="rounded-2xl border border-slate-600 bg-slate-900 px-4 py-4 text-base font-black">{t("guestMarket.login")}</button>
        </div>
      </div>
    </div>
  );
}

// REISOR_STAGE142_DRIVER_GUEST_REGISTRATION_UX_V1
export default function GuestDriverMarketplace({ onLogin, onRegister }) {
  const { t } = useTranslation();
  const [tab] = useState("market");
  const [nativeItems, setNativeItems] = useState([]);
  const [marketItems, setMarketItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [detail, setDetail] = useState(null);
  const [detailType, setDetailType] = useState("");
  const [gate, setGate] = useState(false);

  const items = useMemo(() => {
    if (tab === "native") return nativeItems;

    const seen = new Set();

    return marketItems
      .map((item) => prepareGuestMarketItem(item))
      .filter(Boolean)
      .filter((item) => {
        const key = guestDedupeKey(item);
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
      })
      .sort((a, b) => Number(b?._guestQualityScore || 0) - Number(a?._guestQualityScore || 0))
      .slice(0, 30);
  }, [tab, nativeItems, marketItems]);

  async function load() {
    setLoading(true);
    setError("");
    try {
      if (tab === "native") {
        const data = await getJson("/api/driver/orders");
        setNativeItems(Array.isArray(data?.items) ? data.items : []);
      } else {
        const data = await getJson("/api/driver/freight-market/items?limit=300");
        setMarketItems(Array.isArray(data?.items) ? data.items : []);
      }
    } catch (e) {
      setError(guestMarketSafeError(e, t));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { void load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [tab]);

  // REISOR_HUB_GUEST_DEEP_LINK_V1
  // Open the exact public HUB freight for a guest. Contact access remains server-gated.
  useEffect(() => {
    if (tab !== "market") return;

    let freightId = "";
    try {
      freightId = String(localStorage.getItem("deep_market_freight_id") || "").trim();
    } catch {}

    if (!freightId) return;

    void openItem({ id: freightId });

    // openItem intentionally uses current guest market state.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab]);

  async function openItem(item) {
    if (tab === "native") {
      setDetail(item);
      setDetailType("native");
      return;
    }

    const id = String(item?.id || "").trim();
    if (!id) return;
    setLoading(true);
    setError("");
    try {
      const data = await getJson(`/api/driver/freight-market/items/${encodeURIComponent(id)}`);
      const prepared = prepareGuestMarketItem(data?.item || item);
      setDetail(prepared || item);
      setDetailType("market");
    } catch (e) {
      setError(guestMarketSafeError(e, t, { detail: true }));
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="min-h-screen bg-slate-950 text-white">
      <div className="mx-auto w-full max-w-3xl px-3 pb-28 pt-4 sm:px-5">
        <div className="rounded-3xl border border-slate-800 bg-slate-900/70 p-4 shadow-2xl">
          {/* REISOR_DRIVER_GUEST_LOGIN_VISIBILITY_V1 */}
          <div>
            <div className="text-2xl font-black tracking-tight">REISOR</div>
            <div className="mt-1 text-sm leading-5 text-slate-300">{t("guestMarket.subtitle")}</div>
          </div>

          <div className="mt-4"><LanguageSelector /></div>

          <button
            type="button"
            onClick={onRegister}
            className="mt-4 w-full rounded-2xl bg-blue-600 px-4 py-4 text-base font-black"
          >
            {t("guestMarket.register")}
          </button>

          <div className="mt-4 text-center text-sm font-semibold text-slate-300">
            {t("guestMarket.alreadyHaveAccount")}
          </div>

          <button
            type="button"
            onClick={onLogin}
            className="mt-2 w-full rounded-2xl border border-slate-600 bg-slate-800 px-4 py-3.5 text-base font-black active:bg-slate-700"
          >
            {t("guestMarket.login")}
          </button>
        </div>

        <div className="sticky top-0 z-20 mt-4 rounded-2xl border border-slate-800 bg-slate-950/95 px-4 py-3 backdrop-blur">
          <div className="text-base font-black text-white">{t("guestMarket.marketTab")}</div>
        </div>

        <div className="mt-3 flex items-center justify-between gap-3">
          <div className="text-xs font-semibold text-slate-400">{t("guestMarket.publicHint")}</div>
          <button type="button" onClick={() => void load()} disabled={loading} className="inline-flex items-center gap-2 rounded-xl border border-slate-700 bg-slate-900 px-3 py-2 text-xs font-bold disabled:opacity-50"><RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} />{t("common.refresh")}</button>
        </div>

        {error ? (
          <div
            className="mt-3 rounded-2xl border border-slate-700 bg-slate-900 p-3 text-sm text-slate-200"
            data-reisor-guest-market-internal-code-hidden="v6"
            role="status"
            aria-live="polite"
          >
            {error}
          </div>
        ) : null}
        {loading && !items.length ? <div className="mt-6 text-center text-sm text-slate-400">{t("common.loading")}</div> : null}
        {!loading && !items.length ? <div className="mt-6 rounded-2xl border border-slate-800 bg-slate-900 p-5 text-center text-sm text-slate-400">{t("guestMarket.empty")}</div> : null}

        <div className="mt-3 flex flex-col gap-3">
          {items.map((item, index) => {
            const external = tab === "market";
            const from = String(item?.from || "—");
            const to = String(item?.to || "—");
            const cargo = external ? externalCargo(item) : nativeCargo(item);
            const date = external ? (String(item?.loadingDisplay || "").trim() || loadingDateText(item?.loadingDate)) : dateText(item?.dateTime || item?.whenFrom);
            const price = external ? String(item?.ratePrimary || item?.rateHint || "Договорная") : nativePrice(item, t);
            const weight = external ? weightText(item?.weightKg, true, t) : weightText(item?.weight, false, t);

            return (
              <button key={`${tab}:${String(item?.id || `${item?.from || ""}:${item?.to || ""}:${item?.dateTime || item?.loadingDate || ""}:${index}`)}`} type="button" onClick={() => void openItem(item)} className="w-full rounded-3xl border border-slate-800 bg-slate-900/80 p-4 text-left shadow-lg active:scale-[0.995]">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <div className="text-lg font-black leading-6">{from} → {to}</div>
                    <div className="mt-2 text-sm font-semibold text-slate-300">{cargo}</div>
                  </div>
                  <ChevronRight className="mt-1 h-5 w-5 shrink-0 text-slate-500" />
                </div>
                <div className="mt-4 grid grid-cols-3 gap-2 text-xs">
                  <div className="rounded-xl bg-slate-950/70 p-2"><div className="text-slate-500">{t("guestMarket.date")}</div><div className="mt-1 font-bold text-slate-200">{date}</div></div>
                  <div className="rounded-xl bg-slate-950/70 p-2"><div className="text-slate-500">{t("guestMarket.weight")}</div><div className="mt-1 font-bold text-slate-200">{weight}</div></div>
                  <div className="rounded-xl bg-slate-950/70 p-2"><div className="text-slate-500">{t("guestMarket.price")}</div><div className="mt-1 font-bold text-emerald-300">{price}</div></div>
                </div>
              </button>
            );
          })}
        </div>
      </div>

      {detail ? (
        <div className="fixed inset-0 z-[15000] flex items-end justify-center bg-black/75 p-0 sm:items-center sm:p-4">
          <div className="max-h-[92vh] w-full max-w-xl overflow-y-auto rounded-t-3xl border border-slate-700 bg-slate-950 p-5 shadow-2xl sm:rounded-3xl">
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0 flex-1"><div className="text-xs font-bold uppercase tracking-wide text-blue-300">{detailType === "market" ? t("guestMarket.marketTab") : t("guestMarket.nativeTab")}</div><div className="mt-1 text-xl font-black">{String(detail.from || "—")} → {String(detail.to || "—")}</div></div>
              <button type="button" onClick={() => setDetail(null)} className="rounded-xl bg-slate-800 p-2"><X className="h-5 w-5" /></button>
            </div>

            <div className="mt-4 grid grid-cols-2 gap-3">
              <div className="rounded-2xl border border-slate-800 bg-slate-900 p-4"><div className="flex items-center gap-2 text-xs font-bold text-slate-400"><CalendarDays className="h-4 w-4" />{t("guestMarket.date")}</div><div className="mt-2 font-black">{detailType === "market" ? (String(detail.loadingDisplay || "").trim() || loadingDateText(detail.loadingDate)) : dateText(detail.dateTime || detail.whenFrom)}</div></div>
              <div className="rounded-2xl border border-slate-800 bg-slate-900 p-4"><div className="flex items-center gap-2 text-xs font-bold text-slate-400"><Truck className="h-4 w-4" />{t("guestMarket.weight")}</div><div className="mt-2 font-black">{detailType === "market" ? weightText(detail.weightKg, true, t) : weightText(detail.weight, false, t)}</div></div>
            </div>

            <div className="mt-3 rounded-2xl border border-slate-800 bg-slate-900 p-4 text-sm leading-6 text-slate-300">
              <div><span className="text-slate-500">{t("guestMarket.cargo")}:</span> <b className="text-white">{detailType === "market" ? externalCargo(detail) : nativeCargo(detail)}</b></div>
              <div><span className="text-slate-500">{t("guestMarket.price")}:</span> <b className="text-emerald-300">{detailType === "market" ? String(detail.ratePrimary || detail.rateHint || "—") : nativePrice(detail, t)}</b></div>
              {detailType === "native" && detail.bodyType ? <div><span className="text-slate-500">{t("guestMarket.body")}:</span> <b className="text-white">{String(detail.bodyType)}</b></div> : null}
              {detailType === "native" && detail.comment ? <div className="mt-2 text-slate-400">{String(detail.comment)}</div> : null}
            </div>

            <button type="button" onClick={() => setGate(true)} className="mt-4 w-full rounded-2xl bg-blue-600 px-4 py-4 text-base font-black">{detailType === "market" ? t("guestMarket.showContact") : t("guestMarket.respond")}</button>
            <div className="mt-2 text-center text-xs leading-5 text-slate-500">{t("guestMarket.privateHint")}</div>
          </div>
        </div>
      ) : null}

      {gate ? <GateModal onClose={() => setGate(false)} onLogin={onLogin} onRegister={onRegister} /> : null}
    </div>
  );
}
